// A frame source delivers a live picture plus pose landmarks for each analysed
// frame. The camera source runs MediaPipe on the phone camera; the demo source
// renders and "detects" the virtual surfer. Everything downstream (setup,
// practice loop, recording) is identical for both.

import { LiveSimulator } from '../sim/live';
import { renderScene } from '../sim/render';
import { openCamera } from './camera';
import type { ModelVariant, Settings } from './db';
import { sharedDetector, type Detector } from './pose';

export interface PoseSample {
  /** performance.now() of the frame, ms. */
  t: number;
  lm: Float32Array | null;
}

export interface FrameSource {
  readonly kind: 'camera' | 'demo';
  /** The element showing the live picture. */
  readonly view: HTMLVideoElement | HTMLCanvasElement;
  readonly stream: MediaStream;
  readonly deviceId: string | null;
  readonly label: string | null;
  readonly sim: LiveSimulator | null;
  width(): number;
  height(): number;
  /** Starts delivering analysed frames (replaces any previous listener). */
  start(onFrame: (s: PoseSample) => void): void;
  stop(): void;
  close(): void;
  /** Current picture, downscaled to at most `maxWidth`. */
  snapshot(maxWidth: number): HTMLCanvasElement;
  stats(): { fps: number; inferenceMs: number; delegate: string };
  /** False once the camera track has ended (e.g. iOS reclaimed it in the background). */
  alive(): boolean;
}

class RateMeter {
  private times: number[] = [];
  private costs: number[] = [];
  tick(t: number, cost: number) {
    this.times.push(t);
    this.costs.push(cost);
    while (this.times.length > 30) {
      this.times.shift();
      this.costs.shift();
    }
  }
  fps() {
    const n = this.times.length;
    return n > 1 ? ((n - 1) * 1000) / (this.times[n - 1] - this.times[0]) : 0;
  }
  cost() {
    return this.costs.length ? this.costs.reduce((a, b) => a + b, 0) / this.costs.length : 0;
  }
}

function snapshotOf(el: CanvasImageSource, w: number, h: number, maxWidth: number): HTMLCanvasElement {
  const s = Math.min(1, maxWidth / w);
  const c = document.createElement('canvas');
  c.width = Math.round(w * s);
  c.height = Math.round(h * s);
  c.getContext('2d')!.drawImage(el, 0, 0, c.width, c.height);
  return c;
}

export class CameraSource implements FrameSource {
  readonly kind = 'camera' as const;
  readonly view: HTMLVideoElement;
  readonly sim = null;
  private detector: Detector | null = null;
  private variant: ModelVariant = 'full';
  private failures = 0;
  private switching = false;
  private running = false;
  private handle = 0;
  private onFrame: ((s: PoseSample) => void) | null = null;
  private meter = new RateMeter();

  private constructor(
    readonly stream: MediaStream,
    readonly deviceId: string | null,
    readonly label: string | null,
  ) {
    const v = document.createElement('video');
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    v.setAttribute('playsinline', '');
    v.srcObject = stream;
    this.view = v;
  }

  static async open(settings: Pick<Settings, 'cameraId' | 'resolution' | 'fps'>): Promise<CameraSource> {
    const cam = await openCamera({ deviceId: settings.cameraId, resolution: settings.resolution, fps: settings.fps });
    const src = new CameraSource(cam.stream, cam.deviceId, cam.label);
    await src.view.play().catch(() => {});
    await new Promise<void>((resolve) => {
      if (src.view.videoWidth) resolve();
      else src.view.addEventListener('loadedmetadata', () => resolve(), { once: true });
    });
    return src;
  }

  async useModel(variant: ModelVariant, delegate: 'GPU' | 'CPU'): Promise<Detector> {
    this.variant = variant;
    this.detector = await sharedDetector(variant, delegate);
    this.failures = 0;
    return this.detector;
  }

  alive(): boolean {
    const track = this.stream.getVideoTracks()[0];
    return !!track && track.readyState === 'live';
  }

  /** GPU problems in Safari tend to show up mid-session (lost WebGL context): fall back to CPU. */
  private fallBackToCpu() {
    if (this.switching || this.detector?.delegate === 'CPU') return;
    this.switching = true;
    console.warn('pose detection keeps failing on the GPU; switching to CPU');
    this.detector = null;
    void sharedDetector(this.variant, 'CPU')
      .then((d) => (this.detector = d))
      .catch((e) => console.error('CPU fallback failed', e))
      .finally(() => {
        this.switching = false;
        this.failures = 0;
      });
  }

  width() {
    return this.view.videoWidth;
  }
  height() {
    return this.view.videoHeight;
  }

  start(onFrame: (s: PoseSample) => void): void {
    this.onFrame = onFrame;
    if (this.running) return;
    this.running = true;
    const v = this.view;
    const hasRVFC = 'requestVideoFrameCallback' in v;
    const step = () => {
      if (!this.running) return;
      if (this.detector && v.readyState >= 2 && v.videoWidth) {
        const t = performance.now();
        let lm: Float32Array | null = null;
        try {
          lm = this.detector.detect(v, t);
          this.failures = 0;
        } catch (e) {
          console.warn('pose detection failed', e);
          if (++this.failures >= 8) this.fallBackToCpu();
        }
        this.meter.tick(t, performance.now() - t);
        this.onFrame?.({ t, lm });
      }
      this.handle = hasRVFC ? v.requestVideoFrameCallback(step) : requestAnimationFrame(step);
    };
    this.handle = hasRVFC ? v.requestVideoFrameCallback(step) : requestAnimationFrame(step);
  }

  stop(): void {
    this.running = false;
    if ('cancelVideoFrameCallback' in this.view) this.view.cancelVideoFrameCallback(this.handle);
    cancelAnimationFrame(this.handle);
  }

  close(): void {
    this.stop();
    this.stream.getTracks().forEach((t) => t.stop());
    this.view.srcObject = null;
  }

  snapshot(maxWidth: number): HTMLCanvasElement {
    return snapshotOf(this.view, this.width(), this.height(), maxWidth);
  }

  stats() {
    return { fps: this.meter.fps(), inferenceMs: this.meter.cost(), delegate: this.detector?.delegate ?? '—' };
  }
}

export class DemoSource implements FrameSource {
  readonly kind = 'demo' as const;
  readonly view: HTMLCanvasElement;
  readonly stream: MediaStream;
  readonly sim: LiveSimulator;
  readonly deviceId = null;
  readonly label = 'Demo camera';
  private running = false;
  private handle = 0;
  private last = 0;
  private onFrame: ((s: PoseSample) => void) | null = null;
  private meter = new RateMeter();
  private painter = 0;

  constructor(sim = new LiveSimulator()) {
    this.sim = sim;
    const c = document.createElement('canvas');
    c.width = sim.world.camera.width;
    c.height = sim.world.camera.height;
    this.view = c;
    this.paint(performance.now());
    this.stream = c.captureStream(30);
    // Keep the picture alive even when no listener is attached.
    const loop = (now: number) => {
      if (!this.running) this.paint(now);
      this.painter = requestAnimationFrame(loop);
    };
    this.painter = requestAnimationFrame(loop);
  }

  private paint(now: number) {
    renderScene(this.view.getContext('2d')!, this.sim.world, this.sim.bodyAt(now));
  }

  width() {
    return this.view.width;
  }
  height() {
    return this.view.height;
  }

  start(onFrame: (s: PoseSample) => void): void {
    this.onFrame = onFrame;
    if (this.running) return;
    this.running = true;
    const step = (now: number) => {
      if (!this.running) return;
      // ~30 analysed frames per second, like a phone running the full model.
      if (now - this.last >= 30) {
        this.last = now;
        const t0 = performance.now();
        this.paint(now);
        const lm = this.sim.landmarks(now);
        this.meter.tick(now, performance.now() - t0);
        this.onFrame?.({ t: now, lm });
      }
      this.handle = requestAnimationFrame(step);
    };
    this.handle = requestAnimationFrame(step);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.handle);
  }

  close(): void {
    this.stop();
    cancelAnimationFrame(this.painter);
    this.stream.getTracks().forEach((t) => t.stop());
  }

  snapshot(maxWidth: number): HTMLCanvasElement {
    return snapshotOf(this.view, this.width(), this.height(), maxWidth);
  }

  stats() {
    return { fps: this.meter.fps(), inferenceMs: this.meter.cost(), delegate: 'simulated' };
  }

  alive(): boolean {
    return true;
  }
}
